import React, { lazy, Suspense, useState } from "react";
import { init, loadRemote } from "@module-federation/runtime";
import ReactDOM from "react-dom";
import { Box, Button, Grid, MenuItem, Select, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Function to dynamically initialize Module Federation
const initializeRemoteApp = (remoteUrl: string, remoteScope: string) => {
  init({
    name: "host-app",
    remotes: [
      {
        name: remoteScope,
        entry: `${remoteUrl}/remoteEntry.js`,
        entryGlobalName: remoteScope,
      },
    ],
    shared: {
      react: {
        version: "18.3.1",
        scope: "default",
        lib: () => React,
        shareConfig: {
          singleton: true,
          requiredVersion: "^18.3.1",
        },
      },
      "react-dom": {
        version: "18.2.0",
        scope: "default",
        lib: () => ReactDOM,
        shareConfig: {
          singleton: true,
          requiredVersion: "^18.2.0",
        },
      },
    },
  });
};

// Component to dynamically load and render a remote module
const RemoteComponent: React.FC<{ request: string | null; props?: Record<string, any> }> = ({ request, props }) => {
  const [error, setError] = useState<string | null>(null);

  if (!request) {
    return <Typography variant="h6">No app loaded</Typography>;
  }

  const Component = lazy(() =>
    loadRemote(request)
      .then((module: any) => {
        setError(null); // Clear any previous errors
        return { default: module.default || module };
      })
      .catch((error) => {
        console.error("Failed to load remote module:", error);
        setError("Failed to load the selected app. Please check your options.");
        throw error;
      })
  );

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  return (
    <Suspense fallback={<Typography>Loading remote app...</Typography>}>
      <Component {...props} />
    </Suspense>
  );
};

const DynamicAppLoader: React.FC = () => {
  const theme = useTheme(); // Access the custom theme
  const [remoteModule, setRemoteModule] = useState<string | null>(null);
  const [remoteProps, setRemoteProps] = useState<Record<string, any>>({});
  const [remoteUrl, setRemoteUrl] = useState<string>("http://localhost:3001");
  const [remoteScope, setRemoteScope] = useState<string>("micro_app_1");
  const [remoteEntry, setRemoteEntry] = useState<string>("App");

  const handleRemoteEvent = (data: string) => {
    console.log("Event from remote app:", data);
  };

  const loadRemoteApp = () => {
    if (!remoteUrl || !remoteScope || !remoteEntry) {
      alert("Please select valid options for URL, Scope, and Module.");
      return;
    }

    try {
      initializeRemoteApp(remoteUrl, remoteScope); // Dynamically initialize the remote app
      setRemoteModule(`${remoteScope}/${remoteEntry}`);
      setRemoteProps({
        parentName: `Dynamic ${remoteEntry} Widget`,
        someProp: `This is a dynamically loaded ${remoteEntry}`,
        onRemoteEvent: handleRemoteEvent,
      });
    } catch (error) {
      console.error("Error initializing remote app:", error);
      alert("Failed to load the app. Please check your options.");
    }
  };

  const handleUrlChange = (newUrl: string) => {
    setRemoteUrl(newUrl);
    setRemoteModule(null); // Reset the loaded app when the URL changes
  };

  return (
    <Box
      sx={{
        padding: theme.spacing(4),
        backgroundColor: theme.palette.background.default,
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[2],
        maxWidth: 800,
        margin: "auto",
      }}
    >
      <Typography variant="h4" gutterBottom>
        Dynamic Application Loader
      </Typography>
      <Typography variant="subtitle1" gutterBottom>
        Select the URL, scope, and module to dynamically load a remote app using Module Federation.
      </Typography>
      <Box component="form" noValidate autoComplete="off" sx={{ marginTop: theme.spacing(2) }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(2) }}>
              <Typography variant="body1" sx={{ minWidth: 200, textAlign: "right" }}>
                Remote URL:
              </Typography>
              <Select
                value={remoteUrl}
                onChange={(e) => handleUrlChange(e.target.value)}
                fullWidth
              >
                <MenuItem value="http://localhost:3001">http://localhost:3001</MenuItem>
                <MenuItem value="http://localhost:3002">http://localhost:3002</MenuItem>
              </Select>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(2) }}>
              <Typography variant="body1" sx={{ minWidth: 200, textAlign: "right" }}>
                Scope:
              </Typography>
              <Select
                value={remoteScope}
                onChange={(e) => setRemoteScope(e.target.value)}
                fullWidth
              >
                <MenuItem value="micro_app_1">micro_app_1</MenuItem>
                <MenuItem value="micro_app_2">micro_app_2</MenuItem>
              </Select>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(2) }}>
              <Typography variant="body1" sx={{ minWidth: 200, textAlign: "right" }}>
                Module:
              </Typography>
              <Select
                value={remoteEntry}
                onChange={(e) => setRemoteEntry(e.target.value)}
                fullWidth
              >
                <MenuItem value="App">App</MenuItem>
                <MenuItem value="Card">Card</MenuItem>
              </Select>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={loadRemoteApp}
                size="small" // Make the button smaller
              >
                Load App
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ marginTop: theme.spacing(4) }}>
        {remoteModule ? (
          <RemoteComponent request={remoteModule} props={remoteProps} />
        ) : (
          <Typography>No app loaded</Typography>
        )}
      </Box>
    </Box>
  );
};

export default DynamicAppLoader;